/**
 * Ordered list of supported IANA timezones for clinic configuration —
 * one entry per UTC offset.
 *
 * Display labels are translatable and live under `clinicManagement.timezones.*`
 * in public/locales/en/common.json, keyed by the IANA identifier. The labels
 * show the standard-time UTC offset plus the major cities sharing that offset;
 * actual DST shifts are handled at runtime by Day.js.
 *
 * Ordering: Eastern (EST) first, then the remaining US/Canada zones, then the
 * rest of the world ordered west-to-east by offset.
 *
 * Note: cities grouped under one offset may observe different DST rules, so
 * the stored representative zone is an approximation for places that share its
 * standard offset but not its DST schedule.
 */

/** US + Canada zones, Eastern (EST) pinned first. */
const US_CANADA_TIMEZONES = [
  "America/Toronto",
  "America/St_Johns",
  "America/Halifax",
  "America/Chicago",
  "America/Denver",
  "America/Los_Angeles",
  "America/Anchorage",
  "Pacific/Honolulu",
];

/** Remaining world zones, ordered west-to-east by offset. */
const OTHER_WORLD_TIMEZONES = [
  "Etc/GMT+12",
  "Pacific/Pago_Pago",
  "Pacific/Marquesas",
  "America/Sao_Paulo",
  "America/Noronha",
  "Atlantic/Azores",
  "Europe/London",
  "Europe/Paris",
  "Europe/Athens",
  "Europe/Moscow",
  "Asia/Tehran",
  "Asia/Dubai",
  "Asia/Kabul",
  "Asia/Karachi",
  "Asia/Kolkata",
  "Asia/Kathmandu",
  "Asia/Dhaka",
  "Asia/Yangon",
  "Asia/Bangkok",
  "Asia/Shanghai",
  "Australia/Eucla",
  "Asia/Tokyo",
  "Australia/Adelaide",
  "Australia/Sydney",
  "Australia/Lord_Howe",
  "Pacific/Guadalcanal",
  "Pacific/Auckland",
  "Pacific/Chatham",
  "Pacific/Tongatapu",
  "Pacific/Kiritimati",
];

/** Ordered IANA timezone identifiers offered in the clinic timezone dropdown. */
export const WORLD_TIMEZONES: string[] = [...US_CANADA_TIMEZONES, ...OTHER_WORLD_TIMEZONES];

/** i18n key for a timezone's display label (under clinicManagement.timezones). */
export const getTimezoneLabelKey = (timezone: string): string => `clinicManagement.timezones.${timezone}`;

/** Default timezone for new clinics and the backfill of existing clinics (Eastern). */
export const DEFAULT_CLINIC_TIMEZONE = "America/Toronto";

/** Returns true when the given IANA id is in the supported list. */
export const isSupportedTimezone = (value: string | undefined): value is string => {
  return !!value && WORLD_TIMEZONES.includes(value);
};
